import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { dmTemplateApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, EmptyState, ConfirmDialog } from '../components/common';
import { copyToClipboard } from '../utils/helpers';
import { nicheConfig } from '../theme';

const niches = ['all', ...Object.keys(nicheConfig)];
const stages = ['first_dm', 'follow_up', 'after_call', 'proposal', 'warming_to_dm', 'other'];

const DMTemplates = () => {
  const { showSnackbar } = useApp();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    niche: 'all',
    stage: 'first_dm',
    subject: '',
    body: '',
  });

  const fetchTemplates = async () => {
    try {
      const response = await dmTemplateApi.getAll({ activeOnly: 'false' });
      setTemplates(response.data);
    } catch (error) {
      showSnackbar('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      niche: 'all',
      stage: 'first_dm',
      subject: '',
      body: '',
    });
    setEditDialog(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      niche: template.niche,
      stage: template.stage,
      subject: template.subject || '',
      body: template.body,
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.body) {
      showSnackbar('Name and body are required', 'error');
      return;
    }

    try {
      if (selectedTemplate) {
        await dmTemplateApi.update(selectedTemplate._id, formData);
        showSnackbar('Template updated', 'success');
      } else {
        await dmTemplateApi.create(formData);
        showSnackbar('Template created', 'success');
      }
      setEditDialog(false);
      fetchTemplates();
    } catch (error) {
      showSnackbar('Failed to save template', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await dmTemplateApi.delete(selectedTemplate._id);
      showSnackbar('Template deleted', 'success');
      setDeleteDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      showSnackbar('Failed to delete template', 'error');
    }
  };

  const handleCopy = async (template) => {
    const copied = await copyToClipboard(template.body);
    if (copied) {
      showSnackbar('Template copied to clipboard!', 'success');
    } else {
      showSnackbar('Failed to copy', 'error');
    }
  };

  const getStageLabel = (stage) => {
    const labels = {
      first_dm: 'First DM',
      follow_up: 'Follow Up',
      after_call: 'After Call',
      proposal: 'Proposal',
      warming_to_dm: 'Warming to DM',
      other: 'Other',
    };
    return labels[stage] || stage;
  };

  if (loading) {
    return <LoadingSpinner message="Loading templates..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Create reusable DM templates with variables like {'{{owner_name}}'}, {'{{business_name}}'}, {'{{city}}'}, {'{{niche}}'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New Template
        </Button>
      </Box>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <EmptyState
          title="No templates yet"
          description="Create your first DM template to streamline your outreach"
          actionLabel="Create Template"
          onAction={handleCreate}
        />
      ) : (
        <Grid container spacing={2}>
          {templates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleEdit(template)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setDeleteDialog(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      label={template.niche === 'all' ? 'All Niches' : nicheConfig[template.niche]?.label || template.niche}
                      size="small"
                      sx={{
                        bgcolor: template.niche === 'all' ? 'grey.200' : `${nicheConfig[template.niche]?.color}20`,
                        color: template.niche === 'all' ? 'text.primary' : nicheConfig[template.niche]?.color,
                      }}
                    />
                    <Chip
                      label={getStageLabel(template.stage)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-line',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {template.body}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy(template)}
                  >
                    Copy
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Initial Outreach - Baker"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Niche"
                  value={formData.niche}
                  onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
                >
                  {niches.map((niche) => (
                    <MenuItem key={niche} value={niche}>
                      {niche === 'all' ? 'All Niches' : `${nicheConfig[niche]?.emoji} ${nicheConfig[niche]?.label}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Stage"
                  value={formData.stage}
                  onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                >
                  {stages.map((stage) => (
                    <MenuItem key={stage} value={stage}>
                      {getStageLabel(stage)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Subject (optional)"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="For email templates"
            />
            <TextField
              fullWidth
              label="Message Body"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              multiline
              rows={10}
              placeholder={`Hey {{owner_name}}!

Been following your page - your {{specific_compliment}} is incredible!

Quick question - are you still handling all bookings through DMs?

I help {{niche}}s in {{city}} get booking websites for $40/month (free to build).

Want me to show you what yours could look like?`}
            />
            <Typography variant="caption" color="text.secondary">
              Available variables: {'{{owner_name}}'}, {'{{business_name}}'}, {'{{niche}}'}, {'{{city}}'}, {'{{follower_count}}'}, {'{{instagram_handle}}'}, {'{{specific_compliment}}'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {selectedTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog}
        title="Delete Template"
        message={`Are you sure you want to delete "${selectedTemplate?.name}"?`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog(false)}
      />
    </Box>
  );
};

export default DMTemplates;
